import React, { useEffect, useState, useRef } from 'react';
import { Check, ChevronDown, CheckCircle2, HelpCircle } from 'lucide-react';
import { DimensionValue } from './components/financial/gl-coding/types';
interface DimensionSelectorProps {
  dimensionName: string;
  values: DimensionValue[];
  selectedValue: string | undefined;
  onChange: (value: string) => void;
  disabled?: boolean;
  stepNumber?: number;
  isComplete?: boolean;
  isCurrent?: boolean;
  tooltip?: string;
  required?: boolean;
}
export function DimensionSelector({
  dimensionName,
  values,
  selectedValue,
  onChange,
  disabled = false,
  stepNumber,
  isComplete = false,
  isCurrent = false,
  tooltip,
  required = false
}: DimensionSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [showTooltip, setShowTooltip] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Sync input value with selected value
  useEffect(() => {
    if (selectedValue) {
      const selectedOption = values.find(v => v.code === selectedValue);
      setInputValue(selectedOption ? `${selectedOption.code} - ${selectedOption.description}` : selectedValue);
    } else {
      setInputValue('');
    }
  }, [selectedValue, values]);
  // Filter options based on input
  const filteredOptions = values.filter(option => {
    const searchText = inputValue.toLowerCase();
    return option.code.toLowerCase().includes(searchText) || option.description.toLowerCase().includes(searchText);
  });
  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsOpen(true);
    setHighlightedIndex(0);
    // If user types exact code, select it
    const exactMatch = values.find(v => v.code === newValue);
    if (exactMatch) {
      onChange(exactMatch.code);
    }
  };
  // Handle option selection
  const handleSelectOption = (option: DimensionValue) => {
    setInputValue(`${option.code} - ${option.description}`);
    onChange(option.code);
    setIsOpen(false);
    inputRef.current?.blur();
  };
  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setIsOpen(true);
      return;
    }
    if (!isOpen) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => prev < filteredOptions.length - 1 ? prev + 1 : prev);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredOptions[highlightedIndex]) {
          handleSelectOption(filteredOptions[highlightedIndex]);
        } else if (inputValue) {
          // Allow freeform entry
          onChange(inputValue);
          setIsOpen(false);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        inputRef.current?.blur();
        break;
      case 'Tab':
        setIsOpen(false);
        break;
    }
  };
  // Scroll highlighted option into view
  useEffect(() => {
    if (isOpen && dropdownRef.current && filteredOptions.length > 0) {
      const highlightedElement = dropdownRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: 'nearest'
        });
      }
    }
  }, [highlightedIndex, isOpen, filteredOptions.length]);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  return <div ref={containerRef} className={`relative flex flex-col gap-1.5 transition-all duration-200 ${isCurrent ? 'ring-2 ring-blue-500 ring-opacity-50 rounded-lg p-3 -m-3 bg-blue-50' : ''}`} data-id="element-22928">
      <div className="flex items-center gap-2" data-id="element-22929">
        {stepNumber && <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${isComplete ? 'bg-green-500 text-white' : isCurrent ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'}`} data-id="element-22930">
            {isComplete ? <CheckCircle2 size={16} className="animate-scale-in" data-id="element-22931" /> : stepNumber}
          </div>}
        <label htmlFor={`dimension-${dimensionName}`} className="text-sm font-medium text-gray-900 flex items-center gap-1.5" data-id="element-22932">
          {dimensionName}
          {required && <span className="text-red-500" data-id="element-22933">*</span>}
          {tooltip && <div className="relative" data-id="element-22934">
              <button type="button" onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)} className="text-gray-400 hover:text-gray-600" data-id="element-22935">
                <HelpCircle size={14} data-id="element-22936" />
              </button>
              {showTooltip && <div className="absolute left-0 top-6 z-50 w-64 bg-gray-900 text-white text-xs rounded p-2 shadow-lg" data-id="element-22937">
                  {tooltip}
                </div>}
            </div>}
        </label>
        {isComplete && <CheckCircle2 size={16} className="text-green-500 ml-auto animate-scale-in" data-id="element-22938" />}
      </div>
      <div className="relative" data-id="element-22939">
        <input ref={inputRef} id={`dimension-${dimensionName}`} type="text" value={inputValue} onChange={handleInputChange} onFocus={() => !disabled && setIsOpen(true)} onKeyDown={handleKeyDown} disabled={disabled} placeholder={disabled ? `Complete previous step first` : `Type or select ${dimensionName}...`} className={`w-full px-3 py-2 pr-10 border rounded bg-white text-sm
                     focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                     disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
                     transition-all duration-200 ${isCurrent ? 'border-blue-500 shadow-sm' : 'border-gray-300'}`} data-id="element-22940" />
        <button type="button" onClick={() => !disabled && setIsOpen(!isOpen)} disabled={disabled} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed transition-colors" data-id="element-22941">
          <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-22942" />
        </button>

        {/* Dropdown positioned relative to input wrapper */}
        {isOpen && !disabled && filteredOptions.length > 0 && <div ref={dropdownRef} className="absolute z-[100] left-0 right-0 top-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto animate-fade-in" data-id="element-22943">
            {filteredOptions.map((option, index) => <button key={option.code} type="button" onClick={() => handleSelectOption(option)} onMouseEnter={() => setHighlightedIndex(index)} className={`w-full px-3 py-2 text-left transition-colors ${index === highlightedIndex ? 'bg-blue-50' : ''} ${option.code === selectedValue ? 'bg-blue-100' : 'hover:bg-blue-50'}`} data-id="element-22944">
                <div className="flex items-center justify-between gap-3" data-id="element-22945">
                  <div className="flex-1 min-w-0" data-id="element-22946">
                    <div className="text-sm font-medium text-gray-900" data-id="element-22947">
                      {option.code}
                    </div>
                    <div className="text-xs text-gray-600 truncate" data-id="element-22948">
                      {option.description}
                    </div>
                  </div>
                  {option.code === selectedValue && <Check className="w-4 h-4 text-blue-600 flex-shrink-0" data-id="element-22949" />}
                </div>
              </button>)}
          </div>}

        {isOpen && !disabled && filteredOptions.length === 0 && inputValue && <div className="absolute z-[100] left-0 right-0 top-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-3 animate-fade-in" data-id="element-22950">
            <p className="text-sm text-gray-500" data-id="element-22951">
              No matches found. Press Enter to use "{inputValue}"
            </p>
          </div>}
      </div>
    </div>;
}